import React, { useEffect, useState } from 'react';
import { Flex, Text, SegmentedControl, Paper, Table, Progress, Divider } from '@mantine/core';
import colors from '../utils/colors';
import { IconBike, IconCalendarMonth, IconCalendarWeek, IconFlame, IconHexagonNumber7Filled, IconSparkles, IconStar, IconWeight } from '@tabler/icons-react';
import { useRanking } from '../hooks/useRanking';
import { useProfile } from '../hooks/useProfile';
import { useDashboard } from '../hooks/useDashboard';
import { useLoadingOverlay } from '../components/LoadingOverlayContext';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { staggerContainer, fadeInFromTop, fadeInFromLeft, fadeInFromRight, fadeIn, heartbeat, fadeInFromBottom } from '../utils/animations';
import ScrollAnimatedSection from '../components/ScrollAnimatedSection';

type RankingType = "attendance" | "kcal" | "kg";
type RankingTime = "lastmonth" | "currentmonth";

const Ranking: React.FC = () => {
  const { data, isLoading, error } = useRanking();
  const { data: profileData } = useProfile();
  const { data: dashboardData } = useDashboard();
  const [rankingType, setRankingType] = useState<RankingType>("attendance");
  const [rankingTime, setRankingTime] = useState<RankingTime>("currentmonth");
  const { setLoading } = useLoadingOverlay();
  const { translate } = useLanguage();
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [tableChangeKey, setTableChangeKey] = useState(0);

  useEffect(() => {
    setLoading(isLoading);
    
    if (!isLoading && data) {
      setTimeout(() => setShouldAnimate(true), 50);
    }
  }, [isLoading, data, setLoading]);

  useEffect(() => {
    if (dashboardData?.studio.isStudioBasic && rankingType === "kcal") {
      setRankingType("attendance");
    }
  }, [dashboardData?.studio.isStudioBasic, rankingType]);

  const monthKey = rankingTime === "currentmonth" ? "currentMonth" : "lastMonth";
  const leaderboard = data?.[monthKey] || [];
  const level = data?.level ?? 0;
  const exp = data?.exp ?? 0;
  const expToLevelUp = data?.expToLevelUp ?? 0;
  const scoreTotal = data?.scoreTotal ?? 0;
  const currentLevelMin = data?.currentLevelMin ?? 0;
  const nextLevelMin = data?.nextLevelMin ?? 1000;

  const getValue = (entry: any): number => {
    if (rankingType === "attendance") {
      return entry.days || 0;
    } else if (rankingType === "kcal") {
      return entry.kcal !== undefined ? entry.kcal : (entry._sum?.kcal || 0);
    } else if (rankingType === "kg") {
      return entry.kg !== undefined ? entry.kg : (entry._sum?.kg || 0);
    }
    return 0;
  };

  const fullSortedLeaderboard = [...leaderboard].sort((a, b) => getValue(b) - getValue(a));
  const top10Leaderboard = fullSortedLeaderboard.slice(0, 10);
  
  const currentUserId = profileData?.userId;
  const currentUserPosition = fullSortedLeaderboard.findIndex(user => user.userId === currentUserId);
  const currentUserData = currentUserPosition >= 0 ? fullSortedLeaderboard[currentUserPosition] : null;
  const isUserInTop10 = currentUserPosition >= 0 && currentUserPosition < 10;

  const handleRankingTypeChange = (value: string) => {
    setRankingType(value as RankingType);
    setTableChangeKey(prev => prev + 1);
  };

  const handleRankingTimeChange = (value: string) => {
    setRankingTime(value as RankingTime);
    setTableChangeKey(prev => prev + 1);
  };

  const tableRowVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.12,
        duration: 0.2,
        ease: "easeOut"
      }
    })
  };

  return (
    <motion.div 
      className="p-4 mt-6"
      variants={staggerContainer}
      initial="hidden"
      animate={shouldAnimate ? "visible" : "hidden"}
      exit={{ opacity: 0, transition: { duration: 0.3 } }}
      style={{ willChange: 'transform' }}
      layoutRoot
    >
      <motion.div variants={fadeInFromTop} layout>

        <Flex justify="space-between" align="center" pos="relative">
          <Text c={colors.custom36s} fw={700} fz="12">{`${translate('rankLevel')} ${level}`}</Text>
          <Flex align="center" gap={4} pos="absolute" left="50%" mb={8} style={{ transform: 'translateX(-50%)' }}>
            <IconSparkles size={18} color={colors.custom36p} />
            <Text c={colors.custom36p} fw={700} fz="16">
              {`${scoreTotal} pkt`}
            </Text>
          </Flex>
          <Text c={colors.custom36s} fw={700} fz="12">{`${translate('rankLevel')} ${level + 1}`}</Text>
        </Flex>
          
        <Progress.Root styles={{ root: { backgroundColor: colors.customGray } }} size="xl">
          <Progress.Section color={colors.custom36p} value={(exp/(nextLevelMin - currentLevelMin))*100} animated>
          </Progress.Section>
        </Progress.Root>

        <Flex justify="space-between">
          <Text c={colors.custom36p} fw={700} fz="11">{`${currentLevelMin} pkt`}</Text>
          <motion.div 
            animate="animate" 
            variants={heartbeat}
            className="text-center"
            >
          <Text c={colors.custom36p} fw={700} fz="12" mt={3}>{translate('xpNeeded', { amount: expToLevelUp })}</Text>
          </motion.div>
          <Text c={colors.custom36p} fw={700} fz="11">{`${nextLevelMin} pkt`}</Text>
        </Flex>
      </motion.div>

      <motion.div variants={fadeIn} layout>
        <Divider size="xs" mt={'sm'} mb={'sm'} color={colors.custom36p} />
      </motion.div>
      
      <div className="overflow-x-hidden">
      <motion.div variants={fadeInFromLeft} layout>
        <div className="flex items-center justify-center mb-2">
          <SegmentedControl
            radius="lg"
            size="xs"
            color={`${colors.custom36p}dd`}
            value={rankingType}
            onChange={handleRankingTypeChange}
            data={[
              {
                label: (
                  <Flex gap={3} justify="center" align="center" fz={'14'}>
                    <IconStar size={16} />
                    <span>{translate('attendance')}</span>
                  </Flex>
                ),
                value: "attendance",
              },
              ...(dashboardData?.studio.isStudioBasic ? [] : [{
                label: (
                  <Flex gap={3} justify="center" align="center" fz={'14'}>
                    <IconFlame size={16} />
                    <span>{translate('calories')}</span>
                  </Flex>
                ),
                value: "kcal",
              }]),
              {
                label: (
                  <Flex gap={3} justify="center" align="center" fz={'14'}>
                    <IconWeight size={16} />
                    <span>{translate('kilograms')}</span>
                  </Flex>
                ),
                value: "kg",
              },
            ]}
            w={dashboardData?.studio.isStudioBasic ? 250 : 350}
          />
        </div>
      </motion.div>
      </div>

      <div className="overflow-x-hidden">
      <motion.div variants={fadeInFromRight} layout>
        <div className="flex items-center justify-center mb-2">
          <SegmentedControl
            radius="lg"
            size="xs"
            color={`${colors.custom36p}dd`}
            value={rankingTime}
            onChange={handleRankingTimeChange}
            data={[
              {
                label: (
                  <Flex gap={3} justify="center" align="center" fz={'14'}>
                    <IconCalendarWeek size={16} />
                    <span>{translate('currentMonth')}</span>
                  </Flex>
                ),
                value: "currentmonth",
              },
              {
                label: (
                  <Flex gap={3} justify="center" align="center" fz={'14'}>
                    <IconCalendarMonth size={16} />
                    <span>{translate('previousMonth')}</span>
                  </Flex>
                ),
                value: "lastmonth",
              },
            ]}
            w={300}
          />
        </div>
      </motion.div>
      </div>

      <motion.div variants={fadeInFromBottom} layout>
        <AnimatePresence mode="wait">
          <motion.div 
            key={tableChangeKey}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Paper c={colors.custom36s} bg={`${colors.customGray}cc`} p="md" radius="md" mt="xs">
              <div className="overflow-hidden">
                <Table.ScrollContainer minWidth={100} style={{ overflowY: 'auto' }} type="scrollarea">
                  <Table highlightOnHover horizontalSpacing="xs" verticalSpacing="xs">
                    <Table.Thead>
                      <Table.Tr style={{ color: colors.custom36s, fontWeight: 700 }}>
                        <Table.Th>#</Table.Th>
                        <Table.Th>Ranking TOP 10</Table.Th>
                        <Table.Th style={{textAlign: 'center'}}>
                          {rankingType === 'attendance'
                            ? translate('shortAttendance')
                            : rankingType === 'kcal'
                            ? translate('shortCalories')
                            : translate('shortKilograms')}
                        </Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {top10Leaderboard.map((user: any, index: number) => {
                        let value: number = 0;
                        if (rankingType === 'attendance') {
                          value = user.days || 0;
                        } else if (rankingType === 'kcal') {
                          value = user.kcal !== undefined ? user.kcal : (user._sum?.kcal || 0);
                        } else if (rankingType === 'kg') {
                          value = user.kg !== undefined ? user.kg : (user._sum?.kg || 0);
                        }
                        return (
                          <motion.tr
                            key={user.userId}
                            custom={index}
                            variants={tableRowVariants}
                            initial="hidden"
                            animate="visible"
                            style={{ 
                              display: 'table-row',
                              willChange: 'transform'
                            }}
                          >
                            <Table.Td style={{ backgroundColor: user.userId === currentUserId ? `${colors.custom36p}22` : 'transparent' }}>
                              <Text c={colors.custom36s} fw={700} size="xs">
                                {index + 1}
                              </Text>
                            </Table.Td>
                            <Table.Td style={{ backgroundColor: user.userId === currentUserId ? `${colors.custom36p}22` : 'transparent' }}>
                              <Text
                                c={user.userId === currentUserId ? colors.custom36p : colors.custom36p}
                                fw={user.userId === currentUserId ? 700 : 500}
                                size="sm"
                                style={{
                                  cursor: 'pointer',
                                  whiteSpace: 'nowrap',
                                  textOverflow: 'ellipsis',
                                  overflow: 'hidden',
                                  width: 170,
                                }}
                              >
                                {user.userId === currentUserId 
                                  ? `${user.name} ${user.surname || ''}` 
                                  : `${user.name} ${user.surname?.charAt(0) || ''}.`
                                }
                              </Text>
                            </Table.Td>
                            <Table.Td style={{ backgroundColor: user.userId === currentUserId ? `${colors.custom36p}22` : 'transparent' }}>
                              <Text
                                c={value > 0 ? colors.custom36p : colors.red}
                                fw={user.userId === currentUserId ? 700 : 700}
                                size="md"
                                ta="center"
                                style={{textAlign: 'center'}}
                              >
                                {value}
                              </Text>
                            </Table.Td>
                          </motion.tr>
                        );
                      })}
                      
                      {/* Current user's position if they're outside top 10 */}
                      {!isUserInTop10 && currentUserData && currentUserPosition >= 0 && (
                        <>
                          <Table.Tr>
                            <Table.Td colSpan={3} style={{ padding: 0 }}>
                              <div style={{ 
                                height: '1px', 
                                backgroundColor: colors.custom36p, 
                                margin: '8px 0',
                                opacity: 0.3
                              }} />
                            </Table.Td>
                          </Table.Tr>
                          <motion.tr
                            key={`current-user-${currentUserData.userId}`}
                            custom={11}
                            variants={tableRowVariants}
                            initial="hidden"
                            animate="visible"
                            style={{ 
                              display: 'table-row',
                              willChange: 'transform'
                            }}
                          >
                            <Table.Td style={{ backgroundColor: `${colors.custom36p}22` }}>
                              <Text c={colors.custom36s} fw={700} size="xs">
                                {currentUserPosition + 1}
                              </Text>
                            </Table.Td>
                            <Table.Td style={{ backgroundColor: `${colors.custom36p}22` }}>
                              <Text
                                c={colors.custom36p}
                                fw={700}
                                size="sm"
                                style={{
                                  cursor: 'pointer',
                                  whiteSpace: 'nowrap',
                                  textOverflow: 'ellipsis',
                                  overflow: 'hidden',
                                  width: 170,
                                }}
                                                             >
                                 {currentUserData.name} {currentUserData.surname || ''}
                               </Text>
                            </Table.Td>
                            <Table.Td style={{ backgroundColor: `${colors.custom36p}22` }}>
                              <Text
                                c={getValue(currentUserData) > 0 ? colors.custom36p : colors.red}
                                fw={700}
                                size="md"
                                ta="center"
                                style={{textAlign: 'center'}}
                              >
                                {getValue(currentUserData)}
                              </Text>
                            </Table.Td>
                          </motion.tr>
                        </>
                      )}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </div>
            </Paper>
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

export default Ranking;
